import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Axios from "../../../axios";
import { newAlert } from '../../../Slices/alertSlice';
import "./ResetPassword.css";

const ResetPassword = () => {
  // State definitions
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL parameter extraction
  const vt = searchParams.get("vt");
  const email = searchParams.get("email");

  // Form validation function
  const validateForm = () => {
    const errors = {};

    if (!password) {
      errors.password = "Please enter a password";
    } else if (password.length < 8) {
      errors.password = "Password should be minimum 8 characters";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
    }

    return errors;
  };

  // Effect hook for form validation
  useEffect(() => {
    if (touched.password || touched.confirmPassword) {
      const errors = validateForm();
      setFormErrors(errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, confirmPassword, touched]);

  // Form field blur handler
  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  // Form submission handler
  const handleResetPassword = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true
    });

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length) {
      return;
    }

    // Validate URL parameters
    if (!vt || !email) {
      dispatch(newAlert({ 
        type: "danger", 
        message: "Invalid reset password link. Please request a new password reset." 
      }));
      return;
    }

    setIsLoading(true);
    try {
      const reqBody = { vt, email, password };
      const res = await Axios.post('/api/session/reset-password', reqBody);

      if (res.status === 200) {
        dispatch(
          newAlert({
            type: "success",
            message: res.data?.message || "Password changed successfully!",
          })
        );
        setTimeout(() => navigate("/sign-in"), 1_000);
      }
    } catch (err) {
      const error = err;
      let errorMessage = "An error occurred while resetting your password";
      
      if (error.response) {
        // Handle different error status codes
        const statusCode = error.response.status;
        const errorData = error.response.data;
        
        if (statusCode === 400) {
          // Validation errors
          errorMessage = errorData?.error?.message || 
                        errorData?.message || 
                        "Invalid request. Please check your input.";
        } else if (statusCode === 401) {
          // Authentication/authorization errors (expired or invalid token)
          errorMessage = errorData?.error?.message || 
                        errorData?.message || 
                        "Invalid or expired reset password link. Please request a new one.";
        } else if (statusCode === 422) {
          // Unprocessable entity (validation errors from Joi)
          errorMessage = errorData?.error?.message || 
                        errorData?.message || 
                        "Validation error. Please check your input.";
        } else if (statusCode >= 500) {
          // Server errors
          errorMessage = "Server error. Please try again later.";
        } else {
          // Other errors
          errorMessage = errorData?.error?.message || 
                        errorData?.message || 
                        errorMessage;
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Network error. Please check your connection and try again.";
      } else {
        // Other errors
        errorMessage = error.message || errorMessage;
      }
      
      dispatch(newAlert({ type: "danger", message: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  // Render method
  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <form onSubmit={handleResetPassword}>
          {/* Form header */}
          <h3 className="reset-password-title">Reset Password</h3>
          <p className="reset-password-description">
            Please enter your new password below to reset your account password.
          </p>

          {/* Password input field */}
          <div className="reset-password-input-wrapper">
            <input
              className={`form-control reset-password-input ${
                touched.password && formErrors.password ? "is-invalid" : ""
              }`}
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
            />
            {touched.password && formErrors.password && (
              <div className="invalid-feedback d-block">{formErrors.password}</div>
            )}
          </div>

          {/* Confirm password input field */}
          <div className="reset-password-input-wrapper">
            <input
              className={`form-control reset-password-input ${
                touched.confirmPassword && formErrors.confirmPassword ? "is-invalid" : ""
              }`}
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => handleBlur('confirmPassword')}
            />
            {touched.confirmPassword && formErrors.confirmPassword && (
              <div className="invalid-feedback d-block">{formErrors.confirmPassword}</div>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary reset-password-button"
          >
            {isLoading ? (
              <span
                className="reset-password-spinner"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;

